import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect } from "react";
import type { TestCheck } from "./TestList";
import type { JobLogsResponse, JobDetailsResponse } from "@/hooks/useJobDetails";
import { useTestHistory, type TestHistoryRun } from "@/hooks/useTestHistory";
import { VirtualizedLogViewer } from "./VirtualizedLogViewer";
import { TestHistoryPanel } from "./TestHistoryPanel";
import { TestDetailHeader } from "./TestDetailHeader";
import { safeSessionStorage } from "@/lib/storage";
import { cn } from "@repo/blacksmith-ui/cn";
import { Info, X } from "lucide-react";
import { useLocalStorage } from "usehooks-ts";
import { Button } from "@repo/blacksmith-ui/button";
interface AutoParsedTestDetailViewProps {
  test: TestCheck;
  logs?: JobLogsResponse; // Optional - may be loading
  steps?: JobDetailsResponse["job"]["steps"]; // Optional - may be loading
  isLogsLoading?: boolean;
  // History tab props - history is fetched internally only when History tab is active
  org: string;
  repository: string;
  runId: string;
  jobId: string;
  branch?: string | null;
  timeRangeHours: number;
  branchFilter: "all" | "main" | "current" | "main_and_current" | "latest_per_branch";
  currentBranch?: string | null;
  zoomStart: number | null;
  zoomEnd: number | null;
  onZoomChange: (start: number | null, end: number | null) => void;
  onTimeRangeChange: (hours: number) => void;
  onBranchFilterChange: (filter: "all" | "main" | "current" | "main_and_current" | "latest_per_branch") => void;
  isResizing: boolean;
  onNavigationStart?: (targetRunId: string, targetJobId: string) => void;
  onRunClick: (run: TestHistoryRun) => void;
  showCardErrors: boolean;
  actorMap?: Map<string, string>;
  isSuperuser?: boolean;
}
export const AutoParsedTestDetailView = React.memo(function AutoParsedTestDetailView(t0) {
  const $ = _c(84);
  const {
    test,
    logs,
    isLogsLoading,
    org,
    repository,
    runId,
    jobId,
    timeRangeHours,
    branchFilter,
    currentBranch,
    zoomStart,
    zoomEnd,
    onZoomChange,
    onTimeRangeChange,
    onBranchFilterChange,
    isResizing,
    onNavigationStart,
    onRunClick,
    showCardErrors,
    actorMap,
    isSuperuser: t1
  } = t0;
  const isSuperuser = t1 === undefined ? false : t1;
  const [infoBannerDismissed, setInfoBannerDismissed] = useLocalStorage("auto-parsed-test-info-banner-dismissed", false);
  let t2;
  if ($[0] !== jobId) {
    t2 = () => {
      if (typeof window === "undefined") {
        return "history";
      }
      const stored = safeSessionStorage.get(`testDetailTab_${jobId}`);
      if (stored === "logs" || stored === "history") {
        return stored;
      }
      return "history";
    };
    $[0] = jobId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [activeTab, setActiveTab] = useState(t2);
  let t3;
  let t4;
  if ($[2] !== activeTab || $[3] !== jobId) {
    t3 = () => {
      safeSessionStorage.set(`testDetailTab_${jobId}`, activeTab);
    };
    t4 = [activeTab, jobId];
    $[2] = activeTab;
    $[3] = jobId;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const t5 = test.test_suite ?? null;
  const t6 = test.test_name || "";
  const t7 = activeTab === "history" && !!org && !!repository && !!test.test_name;
  const t8 = currentBranch ?? undefined;
  let t9;
  if ($[6] !== branchFilter || $[7] !== org || $[8] !== repository || $[9] !== t5 || $[10] !== t6 || $[11] !== t7 || $[12] !== t8 || $[13] !== test.id || $[14] !== timeRangeHours) {
    t9 = {
      org,
      repository,
      testSuite: t5,
      testName: t6,
      testType: "auto",
      enabled: t7,
      timeRangeHours,
      branchFilter,
      currentBranch: t8,
      testId: test.id,
      includeSimilarity: true
    };
    $[6] = branchFilter;
    $[7] = org;
    $[8] = repository;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = test.id;
    $[14] = timeRangeHours;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data: testHistory,
    isLoading: isLoadingHistory,
    isFetching: isFetchingHistory
  } = useTestHistory(t9);
  let t10;
  if ($[16] !== activeTab || $[17] !== branchFilter || $[18] !== onBranchFilterChange || $[19] !== onTimeRangeChange || $[20] !== onZoomChange || $[21] !== timeRangeHours || $[22] !== zoomEnd || $[23] !== zoomStart) {
    t10 = <TestDetailHeader activeTab={activeTab} onTabChange={setActiveTab} timeRangeHours={timeRangeHours} onTimeRangeChange={onTimeRangeChange} branchFilter={branchFilter} onBranchFilterChange={onBranchFilterChange} zoomStart={zoomStart} zoomEnd={zoomEnd} onZoomChange={onZoomChange} />;
    $[16] = activeTab;
    $[17] = branchFilter;
    $[18] = onBranchFilterChange;
    $[19] = onTimeRangeChange;
    $[20] = onZoomChange;
    $[21] = timeRangeHours;
    $[22] = zoomEnd;
    $[23] = zoomStart;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = activeTab === "logs" ? "opacity-100 pointer-events-auto z-10" : "opacity-0 pointer-events-none z-0";
  let t12;
  if ($[25] !== t11) {
    t12 = cn("absolute inset-0 transition-opacity duration-150 ease-out", t11);
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = activeTab !== "logs" ? true : undefined;
  const t14 = activeTab !== "logs";
  let t15;
  if ($[27] !== logs) {
    t15 = logs || [];
    $[27] = logs;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== test.log_line_end || $[30] !== test.log_line_start) {
    t16 = test.log_line_start != null && test.log_line_end != null ? {
      start: test.log_line_start,
      end: test.log_line_end
    } : undefined;
    $[29] = test.log_line_end;
    $[30] = test.log_line_start;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = isLogsLoading || !logs;
  const t18 = activeTab === "logs";
  let t19;
  if ($[32] !== t15 || $[33] !== t16 || $[34] !== t17 || $[35] !== t18 || $[36] !== test.id || $[37] !== test.step_order) {
    t19 = <VirtualizedLogViewer logs={t15} stepOrder={test.step_order} highlightRange={t16} isLoading={t17} testId={test.id} isVisible={t18} />;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = test.id;
    $[37] = test.step_order;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t12 || $[40] !== t13 || $[41] !== t14 || $[42] !== t19) {
    t20 = <div className={t12} inert={t13} aria-hidden={t14}>{t19}</div>;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const t21 = activeTab === "history" ? "opacity-100 pointer-events-auto z-10" : "opacity-0 pointer-events-none z-0";
  let t22;
  if ($[44] !== t21) {
    t22 = cn("absolute inset-0 overflow-y-auto flex flex-col transition-opacity duration-150 ease-out", t21);
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  const t23 = activeTab !== "history" ? true : undefined;
  const t24 = activeTab !== "history";
  let t25;
  if ($[46] !== infoBannerDismissed || $[47] !== setInfoBannerDismissed) {
    t25 = !infoBannerDismissed && <div className="px-4 pt-4 pb-2 shrink-0"><div className="relative rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 dark:border-[var(--color-border)]"><div className="flex items-start gap-3"><Info className="h-4 w-4 shrink-0 mt-0.5 text-neutral-950 dark:text-neutral-50" /><p className="text-sm text-neutral-800 dark:text-neutral-50 pr-6">This test suite was automatically parsed from the job logs. For more structured test detection, you can emit a JUnit XML file in your CI pipeline. JUnit XMLs written to disk during the job run will be automatically detected and processed.{" "}<a href="https://docs.blacksmith.sh/blacksmith-observability/test-analytics#auto-parsed-tests" target="_blank" rel="noreferrer" className="underline hover:text-blue-600 font-medium">Learn more</a></p></div><Button onClick={() => setInfoBannerDismissed(true)} className="absolute right-2 top-1/2 -translate-y-1/2 size-6 p-0 bg-transparent hover:bg-neutral-100 dark:hover:bg-neutral-800" variant="subtle" aria-label="Dismiss message"><X className="size-3" /></Button></div></div>;
    $[46] = infoBannerDismissed;
    $[47] = setInfoBannerDismissed;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  const t26 = testHistory || null;
  let t27;
  if ($[49] !== actorMap || $[50] !== branchFilter || $[51] !== currentBranch || $[52] !== isFetchingHistory || $[53] !== isLoadingHistory || $[54] !== isResizing || $[55] !== isSuperuser || $[56] !== jobId || $[57] !== onNavigationStart || $[58] !== onRunClick || $[59] !== onZoomChange || $[60] !== org || $[61] !== repository || $[62] !== runId || $[63] !== showCardErrors || $[64] !== t26 || $[65] !== test.duration_seconds || $[66] !== test.id || $[67] !== test.logs || $[68] !== timeRangeHours || $[69] !== zoomEnd || $[70] !== zoomStart) {
    t27 = <TestHistoryPanel testHistory={t26} isLoadingHistory={isLoadingHistory} isFetchingHistory={isFetchingHistory} testId={test.id} org={org} repository={repository} runId={runId} jobId={jobId} timeRangeHours={timeRangeHours} branchFilter={branchFilter} currentBranch={currentBranch} zoomStart={zoomStart} zoomEnd={zoomEnd} onZoomChange={onZoomChange} onRunClick={onRunClick} isResizing={isResizing} onNavigationStart={onNavigationStart} showCardErrors={showCardErrors} actorMap={actorMap} isSuperuser={isSuperuser} currentRunLogs={test.logs} currentRunDuration={test.duration_seconds} />;
    $[49] = actorMap;
    $[50] = branchFilter;
    $[51] = currentBranch;
    $[52] = isFetchingHistory;
    $[53] = isLoadingHistory;
    $[54] = isResizing;
    $[55] = isSuperuser;
    $[56] = jobId;
    $[57] = onNavigationStart;
    $[58] = onRunClick;
    $[59] = onZoomChange;
    $[60] = org;
    $[61] = repository;
    $[62] = runId;
    $[63] = showCardErrors;
    $[64] = t26;
    $[65] = test.duration_seconds;
    $[66] = test.id;
    $[67] = test.logs;
    $[68] = timeRangeHours;
    $[69] = zoomEnd;
    $[70] = zoomStart;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== t22 || $[73] !== t23 || $[74] !== t24 || $[75] !== t25 || $[76] !== t27) {
    t28 = <div className={t22} inert={t23} aria-hidden={t24}>{t25}{t27}</div>;
    $[72] = t22;
    $[73] = t23;
    $[74] = t24;
    $[75] = t25;
    $[76] = t27;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== t20 || $[79] !== t28) {
    t29 = <div className="flex-1 min-h-0 relative">{t20}{t28}</div>;
    $[78] = t20;
    $[79] = t28;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== t10 || $[82] !== t29) {
    t30 = <div className="flex flex-col h-full">{t10}{t29}</div>;
    $[81] = t10;
    $[82] = t29;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  return t30;
});